import { Add } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Divider, Link, List, ListItem, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import CustomIconButton from '../../components/elements/CustomIconButton';
import SkeletonLoader from '../../components/elements/SkeletonLoader';
import WebLinksModal from '../../components/elements/common/traceability/WebLinkModal';
import { useNotification } from '../../hooks/NotificationContext';
import { getWeblinks, removeWeblink } from '../../modules/traceability';
import ActionMenu from '../../ui/primitives/ActionMenu';
import ConfirmationModal from '../../ui/primitives/ConfirmationModal';
import DetailsCard from '../../ui/layout/DetailsCard';
import { useAccess } from '../../hooks/useAccess';

const WebLinks = ({ entityType, entityId }) => {
  const [weblinks, setWeblinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedWebLink, setSelectedWebLink] = useState(null);
  const { addNotification } = useNotification();

  const { canAccess } = useAccess();
  const canManageWeblinks = canAccess('ManageTraceability');

  const fetchAllWebLinks = async () => {
    setLoading(true);
    try {
      const response = await getWeblinks(entityType, entityId);
      if (response) {
        setWeblinks(response?.data);
        setError(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteWeblink = async () => {
    try {
      await removeWeblink(selectedWebLink.id);
      fetchAllWebLinks();
      addNotification('success', 'Link deleted successfully.');
    } catch {
      addNotification('danger', 'Failed to remove link. Please refresh once and try again!');
    } finally {
      handleConfirmClose();
    }
  };

  const handleEditLink = (link) => {
    setSelectedWebLink(link);
    setShowModal(true);
  };

  const handleShowConfirm = (link) => {
    setSelectedWebLink(link);
    setShowConfirm(true);
  };

  const handleConfirmClose = () => {
    setShowConfirm(false);
    setSelectedWebLink(null);
  };

  useEffect(() => {
    if (entityId) fetchAllWebLinks();
    return () => setWeblinks([]);
  }, [entityId, entityType]);

  const openNewWindow = (url) => {
    const hasProtocol = /^https?:\/\//i;
    const fullUrl = hasProtocol.test(url) ? url : `http://${url}`;
    window.open(fullUrl, '_blank');
  };

  const cardTitle = <Typography variant="h6">Web Links</Typography>;

  const cardActions = canManageWeblinks && (
    <CustomIconButton
      disableRipple
      title="Add Web Link"
      onClick={() => setShowModal(true)}
    >
      <Add color="primary" />
    </CustomIconButton>
  );

  return (
    <>
      <DetailsCard title={cardTitle} actions={cardActions}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <SkeletonLoader rowCount={2} />
        ) : weblinks?.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 3 }}
          >
            No web links
          </Typography>
        ) : (
          <List disablePadding>
            {weblinks.map((link, index) => (
              <Box key={link.url + index}>
                <ListItem
                  disablePadding
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    py: 1.25,
                    px: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    sx={{ minWidth: 20, pt: 0.15, fontWeight: 600, flexShrink: 0 }}
                  >
                    {index + 1}.
                  </Typography>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Link
                        component="button"
                        variant="body2"
                        fontWeight={600}
                        underline="hover"
                        onClick={() => openNewWindow(link.url)}
                        sx={{
                          textAlign: 'left',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                          cursor: 'pointer',
                        }}
                        title={link.url}
                      >
                        {link.url}
                      </Link>
                      <OpenInNewIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
                    </Stack>

                    {link.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {link.description}
                      </Typography>
                    )}
                  </Box>

                  {canManageWeblinks && (
                    <ActionMenu
                      tooltip="Link actions"
                      items={[
                        {
                          label: 'Edit',
                          icon: <EditIcon />,
                          onClick: () => handleEditLink(link),
                        },
                        {
                          label: 'Delete',
                          icon: <DeleteIcon />,
                          color: 'error',
                          onClick: () => handleShowConfirm(link),
                        },
                      ]}
                    />
                  )}
                </ListItem>

                {index < weblinks.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}
      </DetailsCard>

      <WebLinksModal
        show={showModal}
        handleClose={() => {
          setShowModal(false);
          setSelectedWebLink(null);
        }}
        entityId={entityId}
        entityType={entityType}
        fetchAllWebLinks={fetchAllWebLinks}
        selectedWebLink={selectedWebLink}
        addNotification={addNotification}
      />

      <ConfirmationModal
        show={showConfirm}
        handleConfirm={deleteWeblink}
        handleClose={handleConfirmClose}
        title="Remove Web Link"
        body="Are you sure you want to remove this weblink?"
      />
    </>
  );
};

export default WebLinks;